/**
 * services/api.ts  -  Central TanStack Query API functions.
 * FOLDER_RULES §6: all API calls go through this file.
 * Uses the configured Axios client from api/client.ts.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiClient } from "@/api/client";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface JoinRequest {
  id: string;
  status: "pending" | "approved" | "rejected";
  requestedAt: string;
  student: {
    id: string;
    displayName: string;
    avatarConfig: Record<string, unknown>;
    ageGroup: string;
    parent: {
      user: { email: string; country: string; locale: string };
    };
  };
}

export interface LessonSummary {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  phase: string;
  grapheme: string;
  phoneme: string | null;
  ageGroups: string[];
  totalXp: number;
  sortOrder: number;
  thumbnailKey: string | null;
  customVideoUrl: string | null | undefined;
  _count: { tasks: number };
}

export interface SubmissionSummary {
  id: string;
  taskId: string;
  s3Key: string;
  submittedAt: string;
  status: "pending" | "scored" | "returned" | "late";
  isLate: boolean;
  student: {
    id: string;
    displayName: string;
    avatarConfig: Record<string, unknown>;
    ageGroup: string;
  };
  assignment: {
    id: string;
    lessonId: string;
    isHomework: boolean;
    dueDate: string | null;
    class: { id: string; name: string };
  };
  aiSuggestion: {
    suggestedStars: number;
    confidence: number;
    notes: string | null;
  } | null;
  score: { stars: number; scoredAt: string } | null;
  reviewed: boolean;
  reviewedAt: string | null;
  flagged: boolean;
  flaggedAt: string | null;
  flagReason: string | null;
  mediaRemovedAt: string | null;
}

// ─── Query keys ───────────────────────────────────────────────────────────────

export const joinRequestKeys = {
  all: (classId: string) => ["classes", classId, "join-requests"] as const,
};

export const lessonKeys = {
  all: (params?: Record<string, unknown>) => ["lessons", params] as const,
  detail: (id: string) => ["lessons", "detail", id] as const,
};

export const submissionKeys = {
  pending: (classId?: string) => ["submissions", "pending", classId] as const,
  mediaUrl: (id: string) => ["submissions", id, "media-url"] as const,
};

export const voiceAttemptKeys = {
  list: (classId: string, opts?: Record<string, unknown>) =>
    ["voice-attempts", classId, opts] as const,
};

export const analyticsKeys = {
  dashboard: () => ["analytics", "dashboard"] as const,
  classProgress: (classId: string) =>
    ["analytics", "class", classId, "progress"] as const,
};

export const gamificationKeys = {
  leaderboard: (classId: string, period: string) =>
    ["gamification", "leaderboard", classId, period] as const,
  schoolLeaderboard: (period: string) =>
    ["gamification", "school-leaderboard", period] as const,
  classBadges: (classId: string) =>
    ["gamification", "badges", classId] as const,
  studentBadges: (studentId: string) =>
    ["gamification", "badges", "student", studentId] as const,
};

// ─── Gamification types ───────────────────────────────────────────────────────

export interface LeaderboardEntry {
  rank: number;
  studentId: string;
  displayName: string;
  avatarConfig: Record<string, unknown>;
  totalXp: number;
  currentStreak: number;
}

export interface SchoolLeaderboardEntry extends LeaderboardEntry {
  className: string;
}

export interface BadgeInfo {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  xpBonus: number;
  isActive: boolean;
}

export interface StudentBadgeEarned {
  badgeId: string;
  earnedAt: string;
  badge: BadgeInfo;
  student: {
    id: string;
    displayName: string;
    avatarConfig: Record<string, unknown>;
  };
}

// ─── Analytics types ──────────────────────────────────────────────────────────

export interface DashboardStats {
  pendingSubmissions: number;
  unreviewedVoice: number;
  activeStudentsToday: number;
  avgStarsThisWeek: number | null;
  openAssignments: number;
  totalStudents: number;
  classCount: number;
}

export interface StudentProgressSummary {
  id: string;
  displayName: string;
  avatarConfig: Record<string, unknown>;
  totalXp: number;
  streak: number;
  graphemes: {
    grapheme: string;
    masteryLevel: number;
    attemptCount: number;
  }[];
}

// ─── Voice attempt types ──────────────────────────────────────────────────────

export interface VoiceAttemptSummary {
  id: string;
  targetWord: string;
  transcription: string | null;
  isCorrect: boolean;
  confidence: number;
  teacherReviewed: boolean;
  teacherNote: string | null;
  audioKey: string;
  createdAt: string;
  student: {
    id: string;
    displayName: string;
    avatarConfig: Record<string, unknown>;
  };
  lessonTask: {
    prompt: string;
    lesson: { title: string; grapheme: string };
  };
}

// ─── Join request hooks ───────────────────────────────────────────────────────

export function useJoinRequests(classId: string) {
  return useQuery({
    queryKey: joinRequestKeys.all(classId),
    queryFn: () =>
      api
        .get<{ requests: JoinRequest[] }>(`/classes/${classId}/join-requests`)
        .then((d) => d.requests),
    enabled: !!classId,
    refetchInterval: 30_000, // poll so new requests appear without manual refresh
  });
}

export function useReviewJoinRequest(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      action,
    }: {
      requestId: string;
      action: "approve" | "reject";
    }) =>
      api.patch<{ message: string }>(
        `/classes/${classId}/join-requests/${requestId}`,
        { action },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: joinRequestKeys.all(classId) });
      qc.invalidateQueries({ queryKey: ["classes", classId] });
      qc.invalidateQueries({ queryKey: ["classes"] });
    },
  });
}

// ─── Lesson hooks ─────────────────────────────────────────────────────────────

export function useLessons(params?: {
  phase?: string;
  search?: string;
  ageGroup?: string;
}) {
  return useQuery({
    queryKey: lessonKeys.all(params),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params?.phase) qs.set("phase", params.phase);
      if (params?.ageGroup) qs.set("ageGroup", params.ageGroup);
      if (params?.search) qs.set("search", params.search);
      const url = `/lessons${qs.toString() ? `?${qs}` : ""}`;
      return api
        .get<{ lessons: LessonSummary[]; total: number }>(url)
        .then((d) => d);
    },
    staleTime: 5 * 60 * 1000, // lessons change rarely  -  cache for 5 min
  });
}

export function useSetCustomVideo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      lessonId,
      videoUrl,
    }: {
      lessonId: string;
      videoUrl: string | null;
    }) =>
      api.patch<{ lesson: { id: string; customVideoUrl: string | null } }>(
        `/lessons/${lessonId}/custom-video`,
        { videoUrl },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lessons"] }),
  });
}

// ─── Submission hooks ─────────────────────────────────────────────────────────

export function usePendingSubmissions(classId?: string) {
  return useQuery({
    queryKey: submissionKeys.pending(classId),
    // The API pages at 100 max. Follow the cursor so the newest work (which
    // sorts last) is never silently cut off - it used to stop at 50.
    queryFn: async () => {
      const all: SubmissionSummary[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < 20; page++) {
        const qs = new URLSearchParams({ status: "pending", limit: "100" });
        if (classId) qs.set("classId", classId);
        if (cursor) qs.set("cursor", cursor);
        const d = await api.get<{
          submissions: SubmissionSummary[];
          nextCursor: string | null;
        }>(`/submissions?${qs}`);
        all.push(...d.submissions);
        cursor = d.nextCursor;
        if (!cursor) break;
      }
      return { submissions: all, nextCursor: null as string | null };
    },
    enabled: true,
    refetchInterval: 30_000, // poll same cadence as join requests
  });
}

export interface AiSuggestionResult {
  aiSuggestion: {
    suggestedStars: number;
    confidence: number;
    notes: string | null;
    modelVersion: string | null;
    createdAt: string;
  };
}

export function useRequestAiScore(submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<AiSuggestionResult>(`/submissions/${submissionId}/ai-score`),
    onSuccess: () => {
      // Refresh the pending submissions list so the AI badge appears
      void qc.invalidateQueries({ queryKey: submissionKeys.pending() });
    },
  });
}

// ─── Analytics hooks ──────────────────────────────────────────────────────────

export function useDashboardStats() {
  return useQuery({
    queryKey: analyticsKeys.dashboard(),
    queryFn: () => api.get<DashboardStats>("/analytics/dashboard"),
    refetchInterval: 60_000, // refresh every minute
    staleTime: 30_000,
  });
}

export function useClassProgress(classId: string) {
  return useQuery({
    queryKey: analyticsKeys.classProgress(classId),
    queryFn: () =>
      api
        .get<{
          students: StudentProgressSummary[];
        }>(`/analytics/class/${classId}/progress`)
        .then((d) => d.students),
    enabled: !!classId,
    staleTime: 2 * 60 * 1000,
  });
}

// ─── Voice attempt hooks ──────────────────────────────────────────────────────

export function useVoiceAttempts(
  classId: string,
  opts?: { unreviewed?: boolean; studentId?: string },
) {
  return useQuery({
    queryKey: voiceAttemptKeys.list(classId, opts),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (opts?.unreviewed) qs.set("unreviewed", "true");
      if (opts?.studentId) qs.set("studentId", opts.studentId);
      return api
        .get<{
          attempts: VoiceAttemptSummary[];
          total: number;
        }>(`/voice/class/${classId}/attempts${qs.toString() ? `?${qs}` : ""}`)
        .then((d) => d);
    },
    enabled: !!classId,
    refetchInterval: 20_000, // poll  -  new attempts arrive in real-time but also poll as fallback
  });
}

export function useReviewVoiceAttempt(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      attemptId,
      isCorrect,
      teacherNote,
    }: {
      attemptId: string;
      isCorrect: boolean;
      teacherNote?: string;
    }) =>
      api.patch<VoiceAttemptSummary>(`/voice/attempts/${attemptId}`, {
        isCorrect,
        teacherNote,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: voiceAttemptKeys.list(classId) });
    },
  });
}

// ─── Submission media URL ─────────────────────────────────────────────────────

export function useSubmissionMediaUrl(submissionId: string) {
  return useQuery({
    queryKey: submissionKeys.mediaUrl(submissionId),
    queryFn: () =>
      api.get<{ mediaUrl: string; expiresIn: number }>(
        `/submissions/${submissionId}/media-url`,
      ),
    enabled: !!submissionId,
    staleTime: 10 * 60 * 1000, // 10 min  -  URL expires in 15 min, cache for 10
  });
}

// ─── Voice upload URL ─────────────────────────────────────────────────────────

export function useVoiceUploadUrl() {
  return useMutation({
    mutationFn: (params: {
      mimeType: string;
      fileSizeBytes: number;
      submissionId?: string;
      context?: string;
    }) =>
      api.post<{ uploadUrl: string; s3Key: string }>(
        "/media/voice-upload-url",
        params,
      ),
  });
}

export function useScoreSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      stars,
      voiceNoteKey,
      transcript,
      tags,
    }: {
      submissionId: string;
      stars: number;
      voiceNoteKey?: string;
      transcript?: string;
      tags?: string[];
    }) =>
      api.patch<{ xpAwarded: number; teacherName: string }>(
        `/submissions/${submissionId}/score`,
        { stars, voiceNoteKey, transcript, tags },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["submissions"] });
      qc.invalidateQueries({ queryKey: ["classes"] });
      qc.invalidateQueries({ queryKey: ["gamification"] });
    },
  });
}

export function useReviewSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      reviewed = true,
    }: {
      submissionId: string;
      reviewed?: boolean;
    }) =>
      api.patch<{ id: string; reviewed: boolean; reviewedAt: string | null }>(
        `/submissions/${submissionId}/review`,
        { reviewed },
      ),
    onSuccess: (_data, { submissionId }) => {
      qc.setQueriesData<{
        submissions: SubmissionSummary[];
        nextCursor: string | null;
      }>({ queryKey: ["submissions"] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          submissions: old.submissions.map((s) =>
            s.id === submissionId
              ? { ...s, reviewed: _data.reviewed, reviewedAt: _data.reviewedAt }
              : s,
          ),
        };
      });
    },
  });
}

export function useFlagSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      flagged,
      reason,
    }: {
      submissionId: string;
      flagged: boolean;
      reason?: string;
    }) =>
      api.patch<{
        id: string;
        flagged: boolean;
        flaggedAt: string | null;
        flagReason: string | null;
      }>(`/submissions/${submissionId}/flag`, { flagged, reason }),
    onSuccess: (_data, { submissionId }) => {
      qc.setQueriesData<{
        submissions: SubmissionSummary[];
        nextCursor: string | null;
      }>({ queryKey: ["submissions"] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          submissions: old.submissions.map((s) =>
            s.id === submissionId
              ? {
                  ...s,
                  flagged: _data.flagged,
                  flaggedAt: _data.flaggedAt,
                  flagReason: _data.flagReason,
                }
              : s,
          ),
        };
      });
    },
  });
}

export function useRemoveSubmissionMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submissionId: string) =>
      api.delete<{ removed: boolean }>(`/submissions/${submissionId}/media`),
    onSuccess: (_data, submissionId) => {
      qc.setQueriesData<{
        submissions: SubmissionSummary[];
        nextCursor: string | null;
      }>({ queryKey: ["submissions"] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          submissions: old.submissions.map((s) =>
            s.id === submissionId
              ? {
                  ...s,
                  mediaRemovedAt: new Date().toISOString(),
                  reviewed: false,
                }
              : s,
          ),
        };
      });
    },
  });
}

// ─── Gamification hooks ───────────────────────────────────────────────────────

export function useClassLeaderboard(
  classId: string,
  period: "all_time" | "this_week" | "this_month" = "all_time",
) {
  return useQuery({
    queryKey: gamificationKeys.leaderboard(classId, period),
    queryFn: () =>
      api
        .get<{
          period: string;
          entries: LeaderboardEntry[];
        }>(`/gamification/class/${classId}/leaderboard?period=${period}`)
        .then((d) => d.entries),
    enabled: !!classId,
    staleTime: 60_000,
  });
}

export function useSchoolLeaderboard(
  period: "all_time" | "this_week" | "this_month" = "all_time",
) {
  return useQuery({
    queryKey: gamificationKeys.schoolLeaderboard(period),
    queryFn: () =>
      api
        .get<{
          period: string;
          entries: SchoolLeaderboardEntry[];
        }>(`/gamification/school/leaderboard?period=${period}`)
        .then((d) => d.entries),
    staleTime: 60_000,
  });
}

export function useClassBadges(classId: string) {
  return useQuery({
    queryKey: gamificationKeys.classBadges(classId),
    queryFn: () =>
      api
        .get<{
          catalogue: BadgeInfo[];
          studentBadges: StudentBadgeEarned[];
        }>(`/gamification/class/${classId}/badges`)
        .then((d) => d),
    enabled: !!classId,
    staleTime: 2 * 60_000,
  });
}

// ─── Messages types & hooks ───────────────────────────────────────────────────

export interface ThreadSummary {
  threadId: string;
  student: {
    id: string;
    displayName: string;
    avatarConfig: Record<string, unknown>;
  };
  parentEmail: string | null;
  lastMessage: string;
  lastMessageAt: string;
  lastSender: "teacher" | "parent";
  unreadCount: number;
}

export interface MessageDetail {
  id: string;
  sender: "teacher" | "parent";
  body: string;
  sentAt: string;
  readAt: string | null;
}

export const messageKeys = {
  threads: (page?: number) => ["messages", "threads", page] as const,
  thread: (id: string) => ["messages", "thread", id] as const,
  unread: () => ["messages", "unread"] as const,
};

export function useMessageThreads(page = 1) {
  return useQuery({
    queryKey: messageKeys.threads(page),
    queryFn: () =>
      api.get<{ threads: ThreadSummary[]; total: number }>(
        `/messages?page=${page}&limit=50`,
      ),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useMessageThread(threadId: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: messageKeys.thread(threadId),
    queryFn: () =>
      api
        .get<{ messages: MessageDetail[] }>(`/messages/${threadId}`)
        .then((d) => {
          qc.invalidateQueries({ queryKey: messageKeys.unread() });
          qc.invalidateQueries({ queryKey: messageKeys.threads() });
          return d.messages;
        }),
    enabled: !!threadId,
    staleTime: 0,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: messageKeys.unread(),
    queryFn: () =>
      api.get<{ count: number }>("/messages/unread-count").then((d) => d.count),
    refetchInterval: 30_000,
    staleTime: 30_000,
  });
}

export function useNewThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { studentId: string; body: string }) =>
      api.post<{ threadId: string }>("/messages", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["messages"] }),
  });
}

export function useReplyToThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, body }: { threadId: string; body: string }) =>
      api.post<{ ok: boolean }>(`/messages/${threadId}/reply`, { body }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: messageKeys.thread(vars.threadId) });
      qc.invalidateQueries({ queryKey: ["messages", "threads"] });
    },
  });
}

// ─── Settings types & hooks ───────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  locale: string | null;
  avatarUrl: string | null;
  createdAt: string;
  totpEnabled: boolean;
  school: { id: string; name: string; country: string; region: string } | null;
}

export interface SchoolSettings {
  id: string;
  name: string;
  country: string;
  region: string;
  plan: string;
  teacherCount: number;
  studentCount: number;
  classCount: number;
  settings: Record<string, unknown> | null;
}

export const settingsKeys = {
  profile: () => ["settings", "profile"] as const,
  school: () => ["settings", "school"] as const,
};

export function useMyProfile() {
  return useQuery({
    queryKey: settingsKeys.profile(),
    queryFn: () => api.get<UserProfile>("/settings/profile"),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      firstName?: string;
      lastName?: string;
      locale?: string;
    }) =>
      api.patch<{
        id: string;
        firstName: string;
        lastName: string;
        locale: string | null;
      }>("/settings/profile", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: settingsKeys.profile() });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.post<{ ok: boolean }>("/settings/change-password", data),
  });
}

export function useMfaSetup() {
  return useMutation({
    mutationFn: () =>
      api.post<{ secret: string; qrDataUrl: string; backupCodes: string[] }>(
        "/auth/mfa/setup",
      ),
  });
}

export function useMfaEnable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { code: string }) =>
      api.post<{ message: string; backupCodes: string[] }>(
        "/auth/mfa/enable",
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.profile() }),
  });
}

export function useMfaDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { code: string }) =>
      api.post<{ message: string }>("/auth/mfa/disable", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.profile() }),
  });
}

// ─── Boss Battle history ──────────────────────────────────────────────────────

export interface BattleSummary {
  id: string;
  status: "waiting" | "active" | "finished";
  totalRounds: number;
  createdAt: string;
  endedAt: string | null;
  playerCount: number;
  topPlayer: { name: string; score: number } | null;
}

export interface BattlePlayer {
  playerName: string;
  rank: number;
  totalPoints: number;
  correctAnswers: number;
  totalAnswers: number;
  accuracyPct: number;
  avgResponseTimeMs: number;
  student: {
    id: string;
    displayName: string;
    avatarConfig: Record<string, unknown> | null;
  } | null;
}

export interface BattleResults {
  battle: {
    id: string;
    className: string;
    status: string;
    totalRounds: number;
    createdAt: string;
    endedAt: string | null;
  };
  players: BattlePlayer[];
}

export const bossBattleKeys = {
  classBattles: (classId: string) => ["boss-battle", "class", classId] as const,
  results: (battleId: string) => ["boss-battle", "results", battleId] as const,
};

export function useClassBattles(classId: string) {
  return useQuery({
    queryKey: bossBattleKeys.classBattles(classId),
    queryFn: () =>
      api
        .get<{ battles: BattleSummary[] }>(`/boss-battle/class/${classId}`)
        .then((d) => d.battles),
    enabled: !!classId,
    staleTime: 60_000,
  });
}

export function useBattleResults(battleId: string | null) {
  return useQuery({
    queryKey: bossBattleKeys.results(battleId ?? ""),
    queryFn: () => api.get<BattleResults>(`/boss-battle/${battleId}/results`),
    enabled: !!battleId,
    staleTime: 60_000,
  });
}

export function useSchoolSettings() {
  return useQuery({
    queryKey: settingsKeys.school(),
    queryFn: () => api.get<SchoolSettings>("/settings/school"),
    staleTime: 5 * 60_000,
  });
}

// ─── Platform Admin ───────────────────────────────────────────────────────────

export interface AdminSchoolSummary {
  id: string;
  name: string;
  country: string;
  teacherCount: number;
  studentCount: number;
  classCount: number;
  createdAt: string;
}

export interface AdminPlatformStats {
  totalSchools: number;
  totalTeachers: number;
  totalStudents: number;
  totalSubmissions: number;
  activeThisWeek: number;
}

export interface AdminSchoolDetail {
  id: string;
  name: string;
  country: string;
  createdAt: string;
  teachers: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    createdAt: string;
  }[];
  stats: {
    teacherCount: number;
    studentCount: number;
    classCount: number;
  };
}

export const adminKeys = {
  stats: () => ["admin", "stats"] as const,
  schools: () => ["admin", "schools"] as const,
  school: (id: string) => ["admin", "schools", id] as const,
};

export function useAdminStats() {
  return useQuery({
    queryKey: adminKeys.stats(),
    queryFn: () => api.get<AdminPlatformStats>("/admin/stats"),
    staleTime: 60_000,
  });
}

export function useAdminSchools(search?: string) {
  return useQuery({
    queryKey: [...adminKeys.schools(), search],
    queryFn: () =>
      api.get<{ schools: AdminSchoolSummary[] }>(
        `/admin/schools${search ? `?search=${encodeURIComponent(search)}` : ""}`,
      ),
    staleTime: 60_000,
  });
}

export function useAdminSchoolDetail(schoolId: string) {
  return useQuery({
    queryKey: adminKeys.school(schoolId),
    queryFn: () => api.get<AdminSchoolDetail>(`/admin/schools/${schoolId}`),
    staleTime: 60_000,
    enabled: !!schoolId,
  });
}

export function useSuspendSchool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      schoolId,
      suspend,
    }: {
      schoolId: string;
      suspend: boolean;
    }) =>
      api.patch(`/admin/schools/${schoolId}/status`, { suspended: suspend }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.schools() });
    },
  });
}

export interface AdminBadge {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  xpBonus: number;
  isActive: boolean;
  earnCount: number;
}

export function useAdminBadges() {
  return useQuery({
    queryKey: ["admin", "badges"],
    queryFn: () => api.get<{ badges: AdminBadge[] }>("/admin/badges"),
    staleTime: 60_000,
  });
}

export function useUpdateBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      slug,
      isActive,
      xpBonus,
    }: {
      slug: string;
      isActive?: boolean;
      xpBonus?: number;
    }) => api.patch(`/admin/badges/${slug}`, { isActive, xpBonus }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "badges"] });
    },
  });
}

// ─── Platform Settings ────────────────────────────────────────────────────────

export interface PlatformSettings {
  voiceGradingEnabled: boolean;
  voiceConfidenceThreshold: number;
  voiceAutoAcceptThreshold: number;
}

export function usePlatformSettings() {
  return useQuery({
    queryKey: ["admin", "platform-settings"] as const,
    queryFn: () =>
      api.get<{ settings: PlatformSettings }>("/admin/platform-settings"),
    staleTime: 60_000,
  });
}

export function useUpdatePlatformSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<PlatformSettings>) =>
      api.patch<{ settings: PlatformSettings }>(
        "/admin/platform-settings",
        patch,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-settings"] });
    },
  });
}

// ─── Pending School Verification (platform admin) ────────────────────────────

export interface PendingSchool {
  id: string;
  name: string;
  createdAt: string;
  users: { email: string }[];
}

export function usePendingSchools() {
  return useQuery({
    queryKey: ["admin", "pending-schools"] as const,
    queryFn: () =>
      api.get<{ schools: PendingSchool[] }>("/school/admin/pending"),
    staleTime: 30_000,
  });
}

export function useVerifySchool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schoolId: string) =>
      api.post<{ ok: boolean }>(`/school/admin/${schoolId}/verify`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pending-schools"] });
      qc.invalidateQueries({ queryKey: ["admin", "schools"] });
    },
  });
}

// ─── School Admin ─────────────────────────────────────────────────────────────

export interface InviteCode {
  id: string;
  type: "teacher" | "parent_student";
  code: string;
  maxUses: number;
  usedCount: number;
  expiresAt: string;
  createdAt: string;
}

export interface SchoolTeacher {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  classCount: number;
  createdAt: string;
}

export const schoolAdminKeys = {
  teachers: () => ["school-admin", "teachers"] as const,
  inviteCodes: () => ["school-admin", "invite-codes"] as const,
  stats: () => ["school-admin", "stats"] as const,
  activity: () => ["school-admin", "activity"] as const,
};

// ─── School stats & activity types ───────────────────────────────────────────

export interface SchoolStats {
  teachers: number;
  students: number;
  classes: number;
  pendingEnrollments: number;
  lessonsAssignedThisWeek: number;
  activeStudentsToday: number;
}

export type ActivityEvent =
  | { type: "teacher_joined"; name: string; role: string; at: string }
  | { type: "student_joined"; name: string; class: string; at: string }
  | {
      type: "lesson_assigned";
      teacher: string;
      lesson: string;
      class: string;
      at: string;
    }
  | {
      type: "enrollment_request";
      name: string;
      student: string;
      class: string;
      at: string;
    };

export function useSchoolStats() {
  return useQuery({
    queryKey: schoolAdminKeys.stats(),
    queryFn: () =>
      api
        .get<{ stats: SchoolStats }>("/school-admin/stats")
        .then((d) => d.stats),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useSchoolActivity() {
  return useQuery({
    queryKey: schoolAdminKeys.activity(),
    queryFn: () =>
      api
        .get<{ activity: ActivityEvent[] }>("/school-admin/activity")
        .then((d) => d.activity),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useSchoolTeachers() {
  return useQuery({
    queryKey: schoolAdminKeys.teachers(),
    queryFn: () =>
      api.get<{ teachers: SchoolTeacher[] }>("/school-admin/teachers"),
    staleTime: 60_000,
  });
}

export function useSchoolInviteCodes() {
  return useQuery({
    queryKey: schoolAdminKeys.inviteCodes(),
    queryFn: () =>
      api.get<{ codes: InviteCode[] }>("/school-admin/invite-codes"),
    staleTime: 30_000,
  });
}

export function useCreateInviteCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      type: "teacher" | "parent_student";
      maxUses?: number;
      expiresInDays?: number;
    }) => api.post<{ code: InviteCode }>("/school-admin/invite-codes", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: schoolAdminKeys.inviteCodes() });
    },
  });
}

export function useRevokeInviteCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (codeId: string) =>
      api.delete(`/school-admin/invite-codes/${codeId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: schoolAdminKeys.inviteCodes() });
    },
  });
}

export function useDeactivateTeacher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teacherId: string) =>
      api.patch(`/school-admin/teachers/${teacherId}/deactivate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: schoolAdminKeys.teachers() });
    },
  });
}

export interface SchoolBilling {
  license: {
    plan: string;
    maxStudents: number;
    maxTeachers: number;
    features: string[];
    validFrom: string;
    validUntil: string;
    autoRenew: boolean;
    lapsed: boolean;
  } | null;
  usage: {
    teachers: number;
    students: number;
  };
}

export function useSchoolBilling() {
  return useQuery({
    queryKey: ["school-admin", "billing"],
    queryFn: () => api.get<SchoolBilling>("/school-admin/billing"),
    staleTime: 120_000,
  });
}

export interface LicenseSummary {
  plan: string;
  daysLeft: number | null;
  validUntil: string | null;
  autoRenew: boolean;
}

export function useLicenseSummary() {
  return useQuery({
    queryKey: ["school", "license-summary"],
    queryFn: () => api.get<LicenseSummary>("/school/license"),
    staleTime: 300_000,
  });
}

export function useVerificationStatus() {
  return useQuery({
    queryKey: ["school", "verification"],
    queryFn: () =>
      api.get<{ verified: boolean; method: string | null }>(
        "/school/verification",
      ),
    staleTime: 300_000,
  });
}

export function useCreateCheckout() {
  return useMutation({
    mutationFn: (priceKey: string) =>
      api.post<{ url: string }>("/billing/create-checkout-session", {
        priceKey,
      }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export function useCreatePortal() {
  return useMutation({
    mutationFn: () => api.post<{ url: string }>("/billing/customer-portal", {}),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export function useExportSchoolData() {
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.get("/school/export", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "litlings-data-export.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}

export function useIssueCertificate(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      studentId,
      type,
      teacherNote,
      zone,
    }: {
      studentId: string;
      type: string;
      teacherNote?: string;
      zone?: string;
    }) =>
      api.post<{ certificate: { id: string; pdfKey: string } }>(
        `/gamification/student/${studentId}/certificate`,
        { type, teacherNote, zone },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gamificationKeys.classBadges(classId) });
    },
  });
}

// ─── Notifications ────────────────────────────────────────────────────────────
export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}

export function useNotifications() {
  return useQuery({
    queryKey: ["notifications"] as const,
    queryFn: () =>
      api.get<{ notifications: NotificationItem[]; unreadCount: number }>(
        "/notifications",
      ),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.patch("/notifications/read-all", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}
